import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';

import { InfoIcon, TrashIcon } from 'lucide-react';
import { Select, Button, Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row, DefaultTooltip, Switch, ButtonIcon } from 'twyne-new.ui';

import { genderOptions } from 'components/rules/options';
import { titleOptions, stateOptions, dobYearOptions } from 'utils';

import { InnerCardRules, ActionsContainer } from './utils';

const propTypes = {
  value: PropTypes.object,
  field: PropTypes.string,
  cfOptions: PropTypes.array,
  visibility: PropTypes.object,
  onChange: PropTypes.func,
  placeholder: PropTypes.string
};

export default class ControlCardPopulate extends React.Component {

  state = {
    rules: {}
  }

  componentDidMount() {
    const { placeholder = null, actions = [] } = this.props.value || {}
    this.setState({ rules: { placeholder: this.props.placeholder || placeholder, actions } })
  }

  get types() {
    const types = []

    if (this.visibility.prepopulateWith)
      types.push({ label: 'Prepopulate With', value: 'prepopulate_with' })

    if (this.visibility.populateOptions)
      types.push({ label: 'Populate Options', value: 'populate_options' })

    return types
  }

  allOptionsByFieldType = {
    dob: dobYearOptions(18, 100),
    state: stateOptions,
    title: titleOptions,
    gender: genderOptions
  }

  get fieldType() {
    return this.props.field
  }

  get visibility() {
    return this.props.visibility
  }

  get optionsByFieldType() {
    return this.allOptionsByFieldType[this.fieldType] || this.props.cfOptions || []
  }

  InnerCardRulesContent = ({ type, ...props }) => {
    if (type === 'prepopulate_with') {
      return <>
        <this.IPIntelligenceToogle {...props}>
          <FormGroup className="mb-0">
            <Label>Value</Label>
            <this.PopulateWithContent {...props} onChange={value => props.onChange({ value })} />
          </FormGroup>
        </this.IPIntelligenceToogle>
      </>
    }

    if (type === 'populate_options') {
      return <>
        <FormGroup className="mb-0">
          <Label>Options</Label>
          <this.PopulateOptionsContent {...props} onChange={value => props.onChange({ value })} />
        </FormGroup>
      </>
    }

    return null
  }

  handleChange = rules => {
    rules = { ...this.state.rules, ...rules }
    this.setState({ rules })
    this.props.onChange(rules)
  }

  handleChangePlaceholder = evt => {
    const placeholder = evt.target.value
    this.handleChange({ placeholder })
  }

  PopulateOptionsContent = ({ onChange, value }) => {
    const [options, setOptions] = useState(value || [])

    useEffect(() => {
      const selecteds = value || []

      const optionsByType = this.optionsByFieldType
        .filter(option => !selecteds.find(selected => selected.value === option.value))

      setOptions(selecteds.concat(optionsByType))
    }, [value])

    const handleChange = useCallback(items => {
      onChange(items)
    }, [onChange])

    const optionCreator = (str) => {
      const lines = str.split(/[;\n]/)
      return lines.map(line => {
        const [value, label] = line.split(/[:\t]/)
        return { label: (label || value).trim(), value: value.trim() }
      })
    }

    const promptTextCreator = str => {
      return <div>
        {optionCreator(str).map(option => <div>add a new option labeled "{option.label}" with value "{option.value}"</div>)}
      </div>
    }

    const newOptionCreator = ({ label }) => {
      if (typeof label !== 'string')
        return { label }
      const options = optionCreator(label)
      options.label = label
      options.value = label
      return options
    }

    return <InputGroup>
      <div className="form-control p-0">
        <Select
          multi
          creatable
          value={value}
          options={value ? options.concat(value) : options}
          onChange={handleChange}
          isValidNewOption={label => typeof label === 'string' && label.length}
          formatCreateLabel={promptTextCreator}
          newOptionCreator={newOptionCreator}
        />
      </div>
      <InputGroupText addonType="append">
        <Button variant='ghost' color='accent'>
          <DefaultTooltip content={<>
            To add a pair value-label type a colon[:] (e.g. <i>opt_value:opt_label</i>)<br />
            To add multiple options at once type a semicolon[;] (e.g. <i>opt_value1:opt_label1; opt_value2:opt_label2; ...</i>
          </>}>
            <InfoIcon size={14} />
          </DefaultTooltip>
        </Button>
      </InputGroupText>
    </InputGroup>
  }

  PopulateWithContent = ({ onChange, value }) => {
    if (!(['gender', 'title', 'state', 'dob'].includes(this.fieldType) || this.props.cfOptions))
      return <Input
        value={value}
        onChange={evt => onChange(evt.target.value)}
      />

    const promptTextCreator = str => {
      return `add a new option "${str}"`
    }

    const newOptionCreator = ({ label }) => {
      if (typeof label !== 'string')
        return { label }
      return { label: label.trim(), value: label.trim() }
    }

    return <Select
      creatable
      value={value}
      options={this.optionsByFieldType}
      onChange={item => onChange(item && item.value)}
      isValidNewOption={label => typeof label === 'string' && label.length}
      formatCreateLabel={promptTextCreator}
      newOptionCreator={newOptionCreator}
    />
  }

  IPIntelligenceToogle = ({ index, onChange, ipIntelligence = false, children }) => {

    if (!this.visibility.ipIntelligence)
      return children

    const handleToggleIPIntelligence = () => {
      const changes = { ipIntelligence: !ipIntelligence }

      if (changes.ipIntelligence)
        changes.value = ''

      onChange(changes)
    }

    return <>
      <Row>
        <Col size='12'>
          <FormGroup>
            <Label>
              Use Dynamic IP Intelligence
              <DefaultTooltip content='Populate this field from the IP location.'>
                <Button variant='ghost' color='accent'>
                  <InfoIcon size={14} />
                </Button>
              </DefaultTooltip>
            </Label>
            <Switch
              value={ipIntelligence}
              onChange={handleToggleIPIntelligence}
            />
          </FormGroup>
        </Col>
      </Row>
      {ipIntelligence ? null : <Row>
        <Col>{children}</Col>
      </Row>}
    </>
  }

  InnerCardRules = ({ value, type, ...props }) => {
    const { onChange } = props

    const header = <>
      <Select
        value={type}
        options={this.types}
        onChange={({ value }) => onChange({ type: value, value: '' })}
        clearable={false}
        className='w-full'
        onClick={e => e.stopPropagation()}
      />
      <ButtonIcon onClick={() => onChange(null)}>
        <TrashIcon size='16' />
      </ButtonIcon>
    </>

    return <InnerCardRules {...props} header={header} draggable={true} chevClassName=' ' type={type}>
      {type && <this.InnerCardRulesContent {...props} type={type} value={value} />}
    </InnerCardRules>
  }

  Placeholder = () => {
    const { placeholder } = this.state.rules

    return <Col size='8'>
      <FormGroup>
        <Label>Placeholder</Label>
        <Input name="placeholder" value={placeholder || ''} onChange={this.handleChangePlaceholder} />
      </FormGroup>
    </Col>
  }

  render() {
    const { actions } = this.state.rules
    const { placeholder } = this.props

    if (placeholder !== undefined)
      return <this.Placeholder />

    if (this.types.length)
      return <Row>
        <Col size='12'>
          <ActionsContainer
            title='Populate Rules'
            className='max-h-[50vh] overflow-y-auto'
            data={actions}
            onChange={actions => this.handleChange({ actions })}
            Component={this.InnerCardRules}
            onConflict={this.props.onConflict}
          />
        </Col>
      </Row>

    return null
  }
}

ControlCardPopulate.propTypes = propTypes
ControlCardPopulate.defaultProps = {}